import { authApi } from './api/auth';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
// utils
import { getUserActiveRole, setToken, setUserActiveRole } from 'utils';
// types
import { AuthState, MfaData } from 'types/auth';
import { AnalyticsAuthEvents, clearUser as clearAnalyticsUser, trackAnalyticsEvent } from '../../utils/analytics';
import { RootState } from 'store';

const initialState: AuthState = {
  isLoggedIn: false,
  isInitialized: undefined,
  user: null
};

const handleLoginSuccess = (state: AuthState, payload: any) => {
  const { access_token, user, roles } = payload;
  const activeRole = roles[0];

  state.isLoggedIn = true;
  state.isInitialized = true;
  state.user = user;
  state.roles = roles;
  state.activeRole = activeRole;
  setToken(access_token);
};

const slice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      trackAnalyticsEvent(AnalyticsAuthEvents.LOG_OUT, { email: state.user?.email });
      state.isLoggedIn = false;
      state.isInitialized = true;
      state.token = null;
      clearAnalyticsUser();
      setToken(null);
      setUserActiveRole(null);
    },
    setIsInitialized: (state, action: PayloadAction<boolean>) => {
      state.isInitialized = action.payload;
    },
    setMfaStatus: (state, action: PayloadAction<number>) => {
      const mfaObj = typeof state.user?.mfa === 'string' ? JSON.parse(state.user.mfa) : state.user?.mfa ?? {};
      mfaObj.enabled = action.payload;

      if (state.user) {
        state.user.mfa = JSON.stringify(mfaObj);
      }
    },
    setNotificationMethod: (state, action: PayloadAction<number>) => {
      if (state.user) {
        state.user.notification_method_id = action.payload;
      }
    },
    setGlobalError: (state, action: PayloadAction<boolean>) => {
      state.hasGlobalError = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(authApi.endpoints.login.matchFulfilled, (state, { payload }) => {
        if (payload.mfa_enabled) return;
        handleLoginSuccess(state, payload);
      })
      .addMatcher(authApi.endpoints.loginWithMfa.matchFulfilled, (state, { payload }) => {
        handleLoginSuccess(state, payload);
      })
      .addMatcher(authApi.endpoints.me.matchFulfilled, (state, { payload }) => {
        const { user, roles } = payload;
        const storedRole = getUserActiveRole();

        state.isLoggedIn = true;
        state.isInitialized = true;
        state.user = user;
        state.roles = roles;
        state.activeRole = storedRole
          ? (roles.find((r) => r.id === storedRole.id) ?? roles[0])
          : roles[0];
      })
      .addMatcher(authApi.endpoints.getNewToken.matchFulfilled, (state, action) => {
        const { access_token, roles } = action.payload;
        const { role_id } = action.meta.arg.originalArgs;
        const activeRole = roles?.find((x) => x.id === role_id);

        state.activeRole = activeRole;
        state.roles = roles;

        setToken(access_token);
        setUserActiveRole(activeRole);
      });
  }
});
export const { logout, setIsInitialized, setMfaStatus, setNotificationMethod, setGlobalError } = slice.actions;

export default slice.reducer;

export const getUserMfa = (state: RootState): Partial<MfaData> => {
  try {
    return JSON.parse(state.auth.user?.mfa || '{}');
  } catch {
    return {};
  }
};
